import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

/* Identity colour from the name, so a customer looks the same each visit. */
const SWATCH = ['#2563eb', '#0e7490', '#7c3aed', '#157f45', '#a4630c', '#c42028', '#be185d'];
function brandColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return SWATCH[Math.abs(h) % SWATCH.length];
}
function initials(name) {
  return (name || '?').split(/[\s-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function CopyField({ label, value, mono, href }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }).catch(() => {});
  };
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ flex: '0 0 130px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)' }}>{label}</span>
      {href
        ? <a href={href} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, fontSize: 13, wordBreak: 'break-word' }}>{value}</a>
        : <span className={mono ? 'mono' : ''} style={{ flex: 1, minWidth: 0, fontSize: 13, wordBreak: 'break-word' }}>{value}</span>}
      <button type="button" onClick={copy} className="btn btn-ghost" aria-label={`Copy ${label}`}
        style={{ fontSize: 11, padding: '2px 8px', minHeight: 0, flexShrink: 0 }}>
        {copied ? 'copied' : 'copy'}
      </button>
    </div>
  );
}

export default function BCCustomerPage({ jiraUrl }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setTotal(0); setSearched(false); return undefined; }
    setSearching(true);
    const t = setTimeout(() => {
      fetch(`${API}/api/bc/customers?q=${encodeURIComponent(q)}`)
        .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Search failed'); return d; })
        .then(d => { setResults(d.results || []); setTotal(d.total || 0); setError(null); setSearched(true); })
        .catch(e => { setError(e.message); setResults([]); })
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const open = useCallback((number) => {
    setLoading(true);
    fetch(`${API}/api/bc/customers/${encodeURIComponent(number)}`)
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Could not load the customer'); return d; })
      .then(d => { setSelected(d); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // A single hit goes straight to the detail — no point clicking through one row.
  useEffect(() => {
    if (results.length === 1) open(results[0].number);
    if (results.length === 0) setSelected(null);
  }, [results, open]);

  const c = selected;

  return (
    <div>
      <div className="card card-spaced">
        <div className="section-title">Find a customer</div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', margin: '2px 0 12px' }}>
          Search Business Central by name, customer number, VAT number or email.
        </div>
        <input className="input" value={q} onChange={e => setQ(e.target.value)} autoFocus
          placeholder="e.g. Bedimo, BEDIMO, 0861.140.462, compta@…"
          aria-label="Search customers" style={{ width: '100%', maxWidth: 460 }} />
        {searching && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-3)' }}>searching…</span>}
      </div>

      {error && <div className="card card-spaced" role="alert" style={{ color: 'var(--critical)' }}>{error}</div>}

      {searched && results.length > 1 && (
        <div className="card card-spaced">
          <div className="section-title">{total} match{total === 1 ? '' : 'es'}{total > results.length ? ` · showing ${results.length}` : ''}</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Customer</th><th>Number</th><th>VAT</th><th>City</th></tr></thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.number} onClick={() => open(r.number)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td className="mono">{r.number}</td>
                    <td className="mono">{r.vat || '—'}</td>
                    <td>{r.town || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {searched && results.length === 0 && !searching && (
        <div className="card card-spaced" style={{ color: 'var(--text-2)' }}>No customer matches “{q}”.</div>
      )}

      {loading && <div className="card card-spaced" style={{ color: 'var(--text-2)' }}>Loading customer…</div>}

      {c && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div className="card" style={{ flex: '1 1 420px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
              <span aria-hidden="true" style={{ width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                background: brandColor(c.name), color: '#fff', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>{initials(c.name)}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                  {c.number}{c.salesperson ? ` · account manager ${c.salesperson}` : ''}
                </div>
              </div>
              {c.blocked && <span className="badge" style={{ marginLeft: 'auto', background: 'var(--critical-light)', color: 'var(--critical)' }}>Blocked in BC</span>}
            </div>

            <CopyField label="Customer ID" value={c.id} mono />
            <CopyField label="Customer number" value={c.number} mono />
            <CopyField label="VAT number" value={c.vat} mono />
            <CopyField label="Email" value={c.email} href={c.email ? `mailto:${c.email}` : null} />
            <CopyField label="Phone" value={c.phone} href={c.phone ? `tel:${c.phone.replace(/\s/g, '')}` : null} />
            <CopyField label="Address" value={[c.street, c.town, c.country].filter(Boolean).join(', ')} />
            <CopyField label="Website" value={c.website}
              href={c.website ? (/^https?:/.test(c.website) ? c.website : `https://${c.website}`) : null} />
            <div style={{ display: 'flex', gap: 18, paddingTop: 10, fontSize: 12, color: 'var(--text-2)', flexWrap: 'wrap' }}>
              <span>Balance due <b className="mono" style={{ color: 'var(--text)' }}>
                {Number(c.balance_due || 0).toLocaleString('fr-BE', { minimumFractionDigits: 2 })} {c.currency}</b></span>
              {c.last_modified && <span>Updated {c.last_modified.split('-').reverse().join('-')}</span>}
            </div>
          </div>

          <div className="card" style={{ flex: '1 1 320px', minWidth: 0 }}>
            <div className="section-title">Jira</div>
            {c.jira?.length > 0 ? c.jira.map(j => (
              <div key={j.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span className="mono" style={{ fontWeight: 700, color: 'var(--accent)' }}>{j.key}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{j.name}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>{j.tickets} tickets</span>
                {jiraUrl && (
                  <a href={`${jiraUrl}/projects/${j.key}`} target="_blank" rel="noreferrer"
                    className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}>open</a>
                )}
              </div>
            )) : (
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                No Jira project matched this name. The link comes from the Omni365 customer list — add the
                customer name there and it will appear here.
              </div>
            )}

            <div className="section-title" style={{ marginTop: 18 }}>BC projects ({c.projects?.length || 0})</div>
            {c.projects?.length > 0 ? (
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {c.projects.map(p => (
                  <div key={p.number} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span className="mono" style={{ flexShrink: 0, color: 'var(--text-2)' }}>{p.number}</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  </div>
                ))}
              </div>
            ) : <div style={{ fontSize: 13, color: 'var(--text-2)' }}>No projects in Business Central.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
